import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";

export default function DilutionCalculator() {
  const [c1, setC1] = useState("");
  const [v1, setV1] = useState("");
  const [c2, setC2] = useState("");
  const [result, setResult] = useState<number | null>(null);

  const calculate = () => {
    const conc1 = parseFloat(c1);
    const vol1 = parseFloat(v1);
    const conc2 = parseFloat(c2);

    if (isNaN(conc1) || isNaN(vol1) || isNaN(conc2) || conc2 === 0) {
      toast.error("Please enter valid values");
      return;
    }

    const finalVolume = (conc1 * vol1) / conc2;
    setResult(finalVolume);
    toast.success("Calculation complete!");
  };

  const reset = () => {
    setC1("");
    setV1("");
    setC2("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Dilution Calculator" 
      calculatorPath="/calculator/dilution"
      category="chemistry"
      subcategory="Solutions"
    >
        <div className="max-w-2xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold mb-2">Dilution Calculator</h1>
            <p className="text-muted-foreground">
              Calculate dilution from stock solution to final concentration
            </p>
          </div>

          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle>Formula: C₁V₁ = C₂V₂</CardTitle>
              <CardDescription>
                Where C is concentration and V is volume
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="c1">Initial Concentration (C₁)</Label>
                  <Input
                    id="c1"
                    type="number"
                    step="0.001"
                    placeholder="Stock concentration"
                    value={c1}
                    onChange={(e) => setC1(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="v1">Initial Volume (V₁)</Label>
                  <Input
                    id="v1"
                    type="number"
                    step="0.001"
                    placeholder="Stock volume"
                    value={v1}
                    onChange={(e) => setV1(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="c2">Final Concentration (C₂)</Label>
                  <Input
                    id="c2"
                    type="number"
                    step="0.001"
                    placeholder="Desired concentration"
                    value={c2}
                    onChange={(e) => setC2(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <Button onClick={calculate} className="flex-1">
                  <Calculator className="mr-2 h-4 w-4" />
                  Calculate
                </Button>
                <Button onClick={reset} variant="outline">
                  Reset
                </Button>
              </div>

              {result !== null && (
                <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                  <CardHeader>
                    <CardTitle className="text-xl">Final Volume (V₂)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-primary">
                      {result.toFixed(4)}
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      Final volume in same units as V₁
                    </p>
                  </CardContent>
                </Card>
              )}

              <Card className="bg-muted/50">
                <CardHeader>
                  <CardTitle className="text-lg">How to use</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm text-muted-foreground">
                  <p>1. Enter the initial concentration of stock solution</p>
                  <p>2. Enter the volume of stock solution to use</p>
                  <p>3. Enter the desired final concentration</p>
                  <p className="pt-2 font-medium text-foreground">
                    Example: 10 M stock, 5 mL volume, dilute to 2 M = 25 mL final volume
                  </p>
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        </div>
    </PageLayout>
  );
}
